/**
 * usePropertySearch - Custom hook for property search state and logic
 * Extracted from CategoryPage.jsx to reduce component complexity
 * Includes hover-to-zoom map feature with country-specific admin settings
 */
import { useState, useCallback, useMemo, useEffect } from 'react';
import { saveRecentSearch } from '../components/RecentSearches';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function usePropertySearch(initialFilters = {}, country = 'LK') {
  // Property-specific filters
  const [propertyFilters, setPropertyFilters] = useState({
    location: '',
    minPrice: null,
    maxPrice: null,
    bedrooms: 'any',
    bathrooms: 'any',
    propertyType: 'any',
    furnishing: 'any',
    searchScope: 'area',
    radius: null,
    sortBy: 'recent',
    searchCenter: null,
    ...initialFilters
  });

  // View mode state
  const [propertyViewMode, setPropertyViewMode] = useState('list');
  
  // Compare state
  const [compareIds, setCompareIds] = useState([]);
  const [showCompareModal, setShowCompareModal] = useState(false);
  const MAX_COMPARE = 4;

  // Drawn polygon for map search
  const [drawnPolygon, setDrawnPolygon] = useState(null);
  
  // Hover-to-zoom feature settings (admin controlled per country)
  const [hoverZoomSettings, setHoverZoomSettings] = useState({
    enabled: true,
    zoomLevel: 15,
    animationDuration: 500,
    enablePan: true
  });
  const [hoverZoomLoading, setHoverZoomLoading] = useState(false);
  
  // Fetch hover-to-zoom settings from admin
  useEffect(() => {
    const fetchHoverZoomSettings = async () => {
      if (!country) return;
      setHoverZoomLoading(true);
      try {
        const response = await axios.get(`${API_URL}/api/notifications/map-features/${country}`);
        if (response.data) {
          setHoverZoomSettings({
            enabled: response.data.enabled ?? true,
            zoomLevel: response.data.settings?.zoom_level ?? 15,
            animationDuration: response.data.settings?.animation_duration ?? 500,
            enablePan: response.data.settings?.enable_pan ?? true
          });
        }
      } catch (err) {
        console.error('Failed to fetch hover-to-zoom settings:', err);
        // Keep defaults on error
      } finally {
        setHoverZoomLoading(false);
      }
    };
    
    fetchHoverZoomSettings();
  }, [country]);

  // Handle property filter changes with search history
  const handlePropertyFilterChange = useCallback((filters, totalCount) => {
    setPropertyFilters(filters);
    
    // Save to recent searches if filters have meaningful values
    const hasFilters = filters.location || 
      filters.minPrice || 
      filters.maxPrice || 
      (filters.bedrooms && filters.bedrooms !== 'any') ||
      (filters.propertyType && filters.propertyType !== 'any');
    
    if (hasFilters) {
      saveRecentSearch(filters, totalCount || 0);
    }
  }, []);

  // Toggle compare
  const handleCompareToggle = useCallback((listingId) => {
    setCompareIds(prev => {
      if (prev.includes(listingId)) {
        return prev.filter(id => id !== listingId);
      }
      if (prev.length >= MAX_COMPARE) {
        return prev;
      }
      return [...prev, listingId];
    });
  }, []);

  // Clear compare
  const clearCompare = useCallback(() => {
    setCompareIds([]);
  }, []);

  // Handle drawn polygon search
  const handlePolygonDrawn = useCallback((polygon) => {
    setDrawnPolygon(polygon);
  }, []);

  // Build query params from filters
  const buildQueryParams = useCallback(() => {
    const params = new URLSearchParams();
    
    if (propertyFilters.location) params.append('location', propertyFilters.location);
    if (propertyFilters.minPrice) params.append('min_price', propertyFilters.minPrice);
    if (propertyFilters.maxPrice) params.append('max_price', propertyFilters.maxPrice);
    if (propertyFilters.bedrooms && propertyFilters.bedrooms !== 'any') {
      params.append('bedrooms', propertyFilters.bedrooms);
    }
    if (propertyFilters.bathrooms && propertyFilters.bathrooms !== 'any') {
      params.append('bathrooms', propertyFilters.bathrooms);
    }
    if (propertyFilters.propertyType && propertyFilters.propertyType !== 'any') {
      params.append('property_type', propertyFilters.propertyType);
    }
    if (propertyFilters.furnishing && propertyFilters.furnishing !== 'any') {
      params.append('furnished', propertyFilters.furnishing);
    }
    if (propertyFilters.sortBy) params.append('sort_by', propertyFilters.sortBy);
    
    return params.toString();
  }, [propertyFilters]);

  // Get active filter chips for display
  const activeFilters = useMemo(() => {
    const filters = [];
    
    if (propertyFilters.minPrice || propertyFilters.maxPrice) {
      const minLabel = propertyFilters.minPrice ? `Rs.${propertyFilters.minPrice.toLocaleString()}` : 'No min';
      const maxLabel = propertyFilters.maxPrice ? `Rs.${propertyFilters.maxPrice.toLocaleString()}` : 'No max';
      filters.push({ key: 'price', label: `${minLabel} - ${maxLabel}` });
    }
    if (propertyFilters.bedrooms && propertyFilters.bedrooms !== 'any') {
      filters.push({ key: 'bedrooms', label: `${propertyFilters.bedrooms}+ beds` });
    }
    if (propertyFilters.propertyType && propertyFilters.propertyType !== 'any') {
      filters.push({ key: 'propertyType', label: propertyFilters.propertyType });
    }
    if (propertyFilters.furnishing && propertyFilters.furnishing !== 'any') {
      filters.push({ key: 'furnishing', label: propertyFilters.furnishing });
    }
    if (propertyFilters.location) {
      filters.push({ key: 'location', label: propertyFilters.location });
    }
    
    return filters;
  }, [propertyFilters]);

  // Remove a single filter
  const removeFilter = useCallback((key) => {
    setPropertyFilters(prev => {
      const updated = { ...prev };
      if (key === 'price') {
        updated.minPrice = null;
        updated.maxPrice = null;
      } else if (key === 'location') {
        updated.location = '';
        updated.searchCenter = null;
      } else {
        updated[key] = 'any';
      }
      return updated;
    });
  }, []);

  // Clear all filters
  const clearAllFilters = useCallback(() => {
    setPropertyFilters({
      location: '',
      minPrice: null,
      maxPrice: null,
      bedrooms: 'any',
      bathrooms: 'any',
      propertyType: 'any',
      furnishing: 'any',
      searchScope: 'area',
      radius: null,
      sortBy: 'recent',
      searchCenter: null
    });
    setDrawnPolygon(null);
  }, []);

  return {
    // State
    propertyFilters,
    propertyViewMode,
    compareIds,
    showCompareModal,
    drawnPolygon,
    activeFilters,
    hoverZoomSettings,
    hoverZoomLoading,
    
    // Setters
    setPropertyFilters,
    setPropertyViewMode,
    setShowCompareModal,
    
    // Handlers
    handlePropertyFilterChange,
    handleCompareToggle,
    clearCompare,
    handlePolygonDrawn,
    removeFilter,
    clearAllFilters,
    buildQueryParams,
    
    // Constants
    MAX_COMPARE
  };
}
